import type { InventoryItem, MarketplaceAccount, ReOSDatabase } from "./types";
import { createListingDraftsForItemInDatabase, marketplaceListingURLValidationError } from "./workflows";

export const MAX_MANUAL_LISTING_ITEMS = 100;
export interface ManualListingInput {
  accountID: string;
  itemIDs: string[];
  versions: Record<string, string>;
  externalURL?: string;
}
export interface ManualListingResult {
  database: ReOSDatabase;
  updatedItemIDs: string[];
  alreadyListedItemIDs: string[];
  skipped: Array<{ itemID: string; reason: string }>;
}

export function isManualListingAccount(account: MarketplaceAccount) {
  return account.integrationMode !== "api" || account.connectionProfile === "guided";
}

export function manualListingVersion(database: ReOSDatabase, item: InventoryItem, account: MarketplaceAccount) {
  return JSON.stringify([item.updatedAt, account.updatedAt, database.marketplaceListings
    .filter((listing) => listing.itemID === item.id && listing.marketplace === account.marketplace)
    .map((listing) => [listing.id, listing.updatedAt]).sort()]);
}

export function manualListingEligibility(database: ReOSDatabase, itemID: string, account: MarketplaceAccount) {
  const item = database.inventoryItems.find((candidate) => candidate.id === itemID && candidate.workspaceID === account.workspaceID);
  if (!item) return "Item is no longer available.";
  if (item.status === "sold" || item.status === "archived") return "Item is sold or archived.";
  if (item.quantity <= 0) return "Item has no stock available.";
  const listings = database.marketplaceListings.filter((listing) => listing.workspaceID === account.workspaceID && listing.itemID === itemID && listing.marketplace === account.marketplace);
  if (listings.some((listing) => listing.status === "live")) return "Already listed on this channel.";
  if (listings.some((listing) => listing.status === "publishing")) return "A listing is being published. Check its result first.";
  return undefined;
}

export function parseManualListingInput(value: unknown): ManualListingInput {
  const input = value as Partial<ManualListingInput> | null;
  if (!input || typeof input.accountID !== "string" || !input.accountID.trim()
    || !Array.isArray(input.itemIDs) || input.itemIDs.length < 1 || input.itemIDs.length > MAX_MANUAL_LISTING_ITEMS
    || input.itemIDs.some((id) => typeof id !== "string" || !id.trim())
    || !input.versions || typeof input.versions !== "object" || Array.isArray(input.versions)
    || (input.externalURL !== undefined && typeof input.externalURL !== "string")) {
    throw new Error(`Choose a manual channel and between 1 and ${MAX_MANUAL_LISTING_ITEMS} items.`);
  }
  if (input.externalURL && input.itemIDs.length !== 1) throw new Error("Add individual listing URLs from each listing’s details.");
  return { accountID: input.accountID, itemIDs: [...new Set(input.itemIDs)], versions: input.versions, externalURL: input.externalURL?.trim() || undefined };
}

export function markItemsListedManuallyInDatabase(database: ReOSDatabase, value: ManualListingInput, now = new Date()): ManualListingResult {
  const input = parseManualListingInput(value);
  const account = database.marketplaceAccounts.find((candidate) => candidate.id === input.accountID && candidate.workspaceID === database.activeWorkspaceID);
  if (!account || !isManualListingAccount(account)) throw new Error("Choose a channel configured for manual listing.");
  if (input.externalURL) {
    const error = marketplaceListingURLValidationError(account.marketplace, input.externalURL);
    if (error) throw new Error(error);
  }
  const result: ManualListingResult = { database, updatedItemIDs: [], alreadyListedItemIDs: [], skipped: [] };
  const timestamp = now.toISOString();
  for (const itemID of input.itemIDs) {
    const reason = manualListingEligibility(database, itemID, account);
    if (reason) {
      if (reason === "Already listed on this channel.") result.alreadyListedItemIDs.push(itemID);
      else result.skipped.push({ itemID, reason });
      continue;
    }
    const item = database.inventoryItems.find((candidate) => candidate.id === itemID)!;
    if (input.versions[itemID] !== manualListingVersion(database, item, account)) {
      throw new Error("An item or channel changed since this review. Refresh the review before confirming.");
    }
    let listing = result.database.marketplaceListings.find((candidate) => candidate.workspaceID === account.workspaceID && candidate.itemID === itemID
      && candidate.marketplace === account.marketplace && ["draft", "ready", "failed"].includes(candidate.status));
    if (!listing) {
      const created = createListingDraftsForItemInDatabase(result.database, itemID, [account.marketplace], now);
      result.database = created.database;
      listing = created.listings[0];
    }
    result.database = {
      ...result.database,
      inventoryItems: result.database.inventoryItems.map((candidate) => candidate.id === itemID
        ? { ...candidate, status: "listed", listedAt: candidate.listedAt ?? timestamp,
            updatedAt: new Date(Math.max(now.getTime(), new Date(candidate.updatedAt).getTime() + 1)).toISOString() } : candidate),
      marketplaceListings: result.database.marketplaceListings.map((candidate) => candidate.id === listing!.id ? {
        ...candidate, marketplaceAccountID: account.id, status: "live", sourceOrigin: "manual", linkMethod: "guided_manual",
        manualRecordedAt: timestamp, manualRecordedBy: database.currentUserID,
        externalURL: input.externalURL ?? candidate.externalURL, listedAt: candidate.listedAt ?? timestamp, updatedAt: timestamp
      } : candidate)
    };
    result.updatedItemIDs.push(itemID);
  }
  return result;
}
